import * as React from 'react';
import { Building2 } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/app/hooks';
import { activeTenantChanged } from '@/features/auth/authSlice';
import { useGetAccountQuery } from '@/features/account/accountApi';
import { TenantPicker, type TenantOption } from '@/features/account/tenant-picker';
import { CenteredCardLayout } from '@/components/ui/centered-card-layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';

/** The TCIT brand above the card, not the product's wordmark: that one goes in
 * `AppShell` (see §5.5 of the component inventory, `AppShellProps`'s note). */
const TCIT_BRAND = <span className="text-h3 font-extrabold text-fg">TCIT</span>;

/**
 * Screen 3: the tenant selector, **outside** `AppShell` — no tenant has been
 * chosen yet, so there is no sidebar and no identity strip to show.
 *
 * The screen's name says it all: "if there is more than one". With that in
 * mind, `GET /api/account`'s three `tenants` sizes are not the same view with
 * more or fewer rows — they are three different product answers:
 *
 * - **Zero**: it is not an error, it is an account with no organisation yet
 *   (a neutral-toned `EmptyState`).
 * - **Exactly one**: there is no decision to take. Choosing here would be
 *   friction with no purpose, so the organisation is set on its own and the
 *   screen never gets to paint the selector — it redirects straight to `/`.
 *   (`AppLayout` reconciles the active tenant again on every
 *   `GET /api/account` with `availableTenantsResolved`, so this shortcut is
 *   redundant with that reconciliation by design, not a race against it:
 *   either path arrives at the same tenant.)
 * - **More than one**: here there is a real decision. `TenantPicker` in
 *   `layout="list"` lets focus move between organisations without committing
 *   to anything (it is only local state); only on confirming with «Continuar»
 *   is the choice persisted (`activeTenantChanged`) and the app navigated to.
 *   A radiogroup whose arrows already fire `onChange` on every option they
 *   touch cannot also be what fires the navigation: the decision is confirmed
 *   by an explicit action, not by the mere fact of having tabbed past an
 *   option.
 */
export function TenantSelectPage(): React.JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useGetAccountQuery();

  const tenants: readonly TenantOption[] = data?.tenants ?? [];
  const soleTenantId = tenants.length === 1 ? (tenants[0]?.id ?? null) : null;

  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  // With exactly one tenant there is no choice: it is set on its own. The
  // effect lives here and not in the render because dispatching an action
  // during the render breaks React's rules (a side effect outside an event or
  // an effect).
  React.useEffect(() => {
    if (soleTenantId) {
      dispatch(activeTenantChanged(soleTenantId));
    }
  }, [soleTenantId, dispatch]);

  if (soleTenantId) {
    return <Navigate to="/" replace />;
  }

  function handleConfirm(): void {
    if (!selectedId) return;
    dispatch(activeTenantChanged(selectedId));
    navigate('/', { replace: true });
  }

  if (isLoading) {
    return (
      <CenteredCardLayout title="Selecciona una organización" brand={TCIT_BRAND}>
        <div
          role="status"
          aria-label="Cargando tus organizaciones"
          className="flex flex-col gap-stack-sm"
        >
          <Skeleton variant="block" />
          <Skeleton variant="block" />
          <Skeleton variant="block" />
        </div>
      </CenteredCardLayout>
    );
  }

  if (isError) {
    return (
      <CenteredCardLayout title="No se pudo cargar tu cuenta" brand={TCIT_BRAND}>
        <ErrorState error={error} onRetry={refetch} />
      </CenteredCardLayout>
    );
  }

  if (tenants.length === 0) {
    return (
      <CenteredCardLayout
        title="No perteneces a ninguna organización todavía"
        brand={TCIT_BRAND}
      >
        <EmptyState
          icon={Building2}
          tone="neutral"
          title="Aún no perteneces a ninguna organización"
          description="Cuando te inviten a una organización en Factit, va a aparecer aquí."
        />
      </CenteredCardLayout>
    );
  }

  return (
    <CenteredCardLayout
      title="Selecciona una organización"
      description="Tu cuenta pertenece a más de una organización en Factit. Elige con cuál quieres continuar."
      brand={TCIT_BRAND}
    >
      <Card padding="sm">
        <TenantPicker
          tenants={tenants}
          value={selectedId}
          onChange={setSelectedId}
          layout="list"
        />
      </Card>
      <Button
        type="button"
        className="w-full"
        disabled={!selectedId}
        onClick={handleConfirm}
      >
        Continuar
      </Button>
    </CenteredCardLayout>
  );
}
